import { Directive, inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { DialogEvents, DialogSize } from '../definitions/dialog.definitions';
import { FormBase } from '../definitions/forms/form.definitions';
import { BaseViewComponent } from './base-view.component';

@Directive()
export abstract class DialogBaseViewComponent<T = void> extends BaseViewComponent implements OnInit, OnDestroy {
    readonly DialogSize = DialogSize;

    private _afterOpenSubscription!: Subscription;
    private _afterCloseSubscription!: Subscription;
    private _beforeCloseSubscription!: Subscription;
    private _clickedOutsideSubscription!: Subscription;
    protected _primaryActionClicked: boolean = false;
    protected _secondaryActionClicked: boolean = false;
    protected dialogRef!: MatDialogRef<this>;
    protected _data: T;

    protected override _ViewContainer!: ViewContainerRef;

    events: Subject<DialogEvents> = new Subject<DialogEvents>();

    readonly dismissible: boolean = false;
    readonly dismissPrevious: boolean = true;

    constructor() {
        super();

        this.dialogRef = inject(MatDialogRef);
        this._data = inject(MAT_DIALOG_DATA, {optional: true});

        this._afterOpenSubscription = this.dialogRef.afterOpened().subscribe(_ => {
            this.events.next(DialogEvents.AfterOpened);
        });

        this._afterCloseSubscription = this.dialogRef.afterClosed().subscribe(_ => {
            this.events.next(DialogEvents.AfterClosing);
            if (this._primaryActionClicked) {
                this.events.next(DialogEvents.AfterClosingFromPrimaryAction);
            }
            this._afterCloseSubscription.unsubscribe(); //We unsubrscribe here instead of onDestroy, otherwise this subscription has been cleared before we get here!
        });

        this._beforeCloseSubscription = this.dialogRef.beforeClosed().subscribe(_ => {
            FormBase.ignoreCreatedForms = false;
            this.events.next(DialogEvents.BeforeClosing);
            if (this._primaryActionClicked) {
                this.events.next(DialogEvents.BeforeClosingFromPrimaryAction);
            }
        });

        this._clickedOutsideSubscription = this.dialogRef.backdropClick().subscribe(_ => {
            this.events.next(DialogEvents.ClosingFromClickedOutside);
        });
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this._afterOpenSubscription.unsubscribe();
        // this._afteCloseSubscription.unsubscribe(); //Note: See Above
        this._beforeCloseSubscription.unsubscribe();
        this._clickedOutsideSubscription.unsubscribe();
    }

    DismissAction() {
        this.events.next(DialogEvents.Dismissed);
        this.dialogRef.close();
    }

    PrimaryAction() {
        this._primaryActionClicked = true;
        this.events.next(DialogEvents.PrimaryAction);
    }

    SecondaryAction() {
        this._secondaryActionClicked = true;
        this.events.next(DialogEvents.SecondaryAction);
    }

    EnablePrimaryAction(): void {
        this._primaryActionClicked = false;
    }

    get secondaryActionClicked(): boolean {
        return this._secondaryActionClicked;
    }

    get primaryActionClicked(): boolean {
        return this._primaryActionClicked;
    }

    get data(): T {
        return this._data;
    }
}
